import { createAsyncThunk, createSlice, isRejected, PayloadAction } from "@reduxjs/toolkit";
import { axiosGet, axiosPost, axiosPut } from "utils/APIHelper";
import { brandDeadlinesType, DISPLAY_TYPE, initiativePlansType, PAGE_SIZE } from "utils/CommonConstants";
import { responseTypes, serviceTypes } from "utils/Types";
import { fetchSuggestedPlansIdAsync } from "../library/Suggested/Suggested.slice";

const configTabSelectedInitialState = {
  [DISPLAY_TYPE.highlights]: 0,
  [DISPLAY_TYPE.myPlansOverview]: 0,
  [DISPLAY_TYPE.userDashboardMyPlans]: 0,
  [DISPLAY_TYPE.userDashboardHighlights]: 0,
  [DISPLAY_TYPE.bookmarkedPropertiesPlans]: 0
};
const initialState: any = {
  dashboardReport: {
    CARDS_SNAPSHOT: [],
    PLANS_SNAPSHOT: []
  },
  plansReport: { list: [], totalRecords: 0 },
  cardsReport: { list: [], totalRecords: 0 },
  brandDeadlines: { list: [], totalRecords: 0 },
  initiativePlans: { list: [], totalRecords: 0 },
  status: {
    deadlinePlansAPI: false,
    dashboardReportAPI: false,
    dashboardPlansReportAPI: false
  },
  dashboardMetrics: {},
  shouldShowEmailDialog: false,
  savedFiltersAndLists: {},
  isFilterApiCalled: false,
  bookMarkInncodes: [],
  configTabSelected: configTabSelectedInitialState
};

export const fetchDashboardPlansAsync = createAsyncThunk("plans/fetchPlans", async (data: any, thunkApi) => {
  const API_URL = `plans?externalHltId=${data?.externalHltId || ""}`;
  const response: any = await axiosPost(API_URL, "", data);

  if (
    response?.response?.status >= responseTypes.internal_error &&
    response?.response?.status < responseTypes.other_error
  ) {
    return thunkApi.rejectWithValue(response.response);
  }

  return response.data;
});

export const fetchDashboardReportAsync = createAsyncThunk("dashboard/plansReport", async (data: any, thunkApi) => {
  const { innCode, list } = data;
  const { rejectWithValue } = thunkApi;
  const API_URL = `plans/reports?innCode=${innCode}&query=${list}&externalHltId=${data?.externalHltId || ""}`;
  const response: any = await axiosGet(API_URL, "userplan");
  if (response.status !== responseTypes.success) {
    return rejectWithValue(response.response.data);
  }
  return response.data;
});

export const fetchPlansOverviewReportAsync = createAsyncThunk(
  "dashboard/plansOverview",
  async (data: any, thunkApi) => {
    const { innCode, page } = data;
    const API_URL = `plans/reports/plans/overview?page=${page}&size=${PAGE_SIZE}&innCode=${innCode}&externalHltId=${
      data?.externalHltId || ""
    }`;
    const response: any = await axiosGet(API_URL, serviceTypes.userplan);
    if (
      response?.response?.status >= responseTypes.internal_error &&
      response?.response?.status < responseTypes.other_error
    ) {
      return thunkApi.rejectWithValue(response.response);
    }
    return response.data;
  }
);

export const fetchCardsOverviewReportAsync = createAsyncThunk("dashboard/cardsOverview", async (data: any) => {
  const { innCode, page } = data;
  const API_URL = `plans/reports/cards/overview?page=${page}&size=${PAGE_SIZE}&innCode=${innCode}&externalHltId=${
    data?.externalHltId || ""
  }`;
  const response: any = await axiosGet(API_URL, serviceTypes.userplan);
  return response.data;
});

export const fetchBookmarkedInncodeAsync = createAsyncThunk(`bookmarked_inncodes`, async (data: {}, thunkApi) => {
  const API_URL = `bookmarks`;
  const response = await axiosGet(API_URL, "userplan");
  return response.data;
});

export const updateBookmarkedInncodesAsync = createAsyncThunk(
  "update_bookmarked_inncodes",
  async (data: any, thunkApi) => {
    const { shouldUpdateState, ...payload } = data;
    const API_URL = `bookmarks`;
    const response: any = await axiosPut(API_URL, "userplan", payload);
    if (response?.status !== responseTypes.success) {
      return thunkApi.rejectWithValue(response.response.data);
    }
    return response.data;
  }
);

export const DashboardSlice = createSlice({
  name: "Dashboard",
  initialState,
  reducers: {
    clearDashboardData: (state) => {
      state.dashboardReport = initialState.dashboardReport;
      state.plansReport = initialState.plansReport;
      state.cardsReport = initialState.cardsReport;
      state.brandDeadlines = initialState.brandDeadlines;
      state.initiativePlans = initialState.initiativePlans;
      state.status = initialState.status;
      state.dashboardMetrics = initialState.dashboardMetrics;
      state.shouldShowEmailDialog = initialState.shouldShowEmailDialog;
    },
    setOpenEmailDialog: (state, action) => {
      state.shouldShowEmailDialog = action.payload;
    },
    setBookmarkedInncodes: (state, action) => {
      state.bookMarkInncodes = action.payload;
    },
    setConfigTabSelected: (state, action) => {
      state.configTabSelected[action.payload.type] = action.payload.val;
    },
    resetConfigTabSelected: (state, action) => {
      state.configTabSelected = configTabSelectedInitialState;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardReportAsync.fulfilled, (state, action: PayloadAction<any>) => {
        state.status.dashboardReportAPI = true;
        state.dashboardReport = action.payload;
      })
      .addCase(fetchPlansOverviewReportAsync.fulfilled, (state, action: any) => {
        state.status.dashboardPlansReportAPI = true;
        state.plansReport.list =
          action.meta.arg.page === 0
            ? action.payload.PLANS_OVERVIEW
            : [...state.plansReport.list, ...action.payload.PLANS_OVERVIEW];
        state.plansReport.totalRecords = action?.payload?.meta?.page?.total;
      })
      .addCase(fetchCardsOverviewReportAsync.fulfilled, (state, action: any) => {
        state.cardsReport.list =
          action.meta.arg.page === 0
            ? action.payload.CARDS_OVERVIEW
            : [...state.cardsReport.list, ...action.payload.CARDS_OVERVIEW];
        state.cardsReport.totalRecords = action?.payload?.meta?.page?.total;
      })
      .addCase(fetchDashboardReportAsync.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(fetchDashboardPlansAsync.fulfilled, (state, action: any) => {
        if (action?.meta?.arg?.planType[0] === brandDeadlinesType) {
          state.status.deadlinePlansAPI = true;
          state.brandDeadlines.list =
            action.meta.arg.pagination.page === 1
              ? action.payload.plans
              : [...state.brandDeadlines.list, ...action.payload.plans];
          state.brandDeadlines.totalRecords = action?.payload?.meta?.page?.total;
        } else if (action.meta.arg.planType[0] === initiativePlansType) {
          state.initiativePlans.list =
            action.meta.arg.pagination.page === 1
              ? action.payload.plans
              : [...state.initiativePlans.list, ...action.payload.plans];
          state.initiativePlans.totalRecords = action?.payload?.meta?.page?.total;
        }
      })
      .addCase(fetchSuggestedPlansIdAsync.fulfilled, (state, action: any) => {
        state.dashboardMetrics = action.payload.dashboardMetrics;
      })

      .addCase(fetchBookmarkedInncodeAsync.fulfilled, (state, action: any) => {
        state.bookMarkInncodes = action.payload?.bookmarks || [];
      })
      .addCase(updateBookmarkedInncodesAsync.fulfilled, (state, action: any) => {
        if (action.meta.arg.shouldUpdateState !== false) state.bookMarkInncodes = action.payload.bookmarks;
      })
      .addMatcher(isRejected, (state, action) => {
        state.status = {
          deadlinePlansAPI: true,
          dashboardReportAPI: true,
          dashboardPlansReportAPI: true
        };
      });
  }
});

export const {
  clearDashboardData,
  setOpenEmailDialog,
  setBookmarkedInncodes,
  setConfigTabSelected,
  resetConfigTabSelected
} = DashboardSlice.actions;
export default DashboardSlice.reducer;
